import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import {
  AuthShell,
  focusFirstError,
  PasswordField,
  PRIMARY_BUTTON_CLASS,
} from "../components/auth-form.tsx";
import { ErrorMessage } from "../components/legacy/ui.tsx";
import { authClient } from "../lib/auth-client.ts";
import { collect, type Errors, validatePassword } from "../lib/auth-validation.ts";

export const Route = createFileRoute("/password-reset")({
  component: PasswordReset,
  validateSearch: (s: Record<string, unknown>) => ({
    token: typeof s.token === "string" ? s.token : "",
  }),
});

function PasswordReset() {
  const navigate = useNavigate();
  const { token } = Route.useSearch();
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<Errors<"password">>({});
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState("");
  const [busy, setBusy] = useState(false);

  const check = () => collect<"password">({ password: validatePassword(password) });

  const reset = async () => {
    setSubmitted(true);
    setFormError("");
    const found = check();
    setErrors(found);
    if (Object.keys(found).length > 0) {
      focusFirstError(found);
      return;
    }

    setBusy(true);
    const result = await authClient.resetPassword({ newPassword: password, token });
    setBusy(false);
    if (result.error) {
      setFormError(result.error.message ?? "Could not reset password");
      return;
    }
    // Resetting does not create a session, so signing in is the next step.
    navigate({ to: "/sign-in" });
  };

  if (!token) {
    return (
      <AuthShell title="Reset your password">
        <div className="space-y-4 text-sm text-gray-700">
          <p>This link is missing its reset token, so it cannot be used.</p>
          <Link
            to="/forgot-password"
            className="inline-block font-medium text-primary-600 hover:text-primary-500"
          >
            Request a new reset link
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Reset your password" subtitle="Choose a new password for your account.">
      <form
        noValidate
        className="space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
          reset();
        }}
      >
        {formError !== "" && (
          <ErrorMessage
            heading="We ran into a problem resetting your password!"
            message={formError}
          />
        )}

        <PasswordField
          label="New password"
          name="password"
          autoComplete="new-password"
          showRule
          value={password}
          error={errors.password}
          onChange={(v) => {
            setPassword(v);
            if (submitted) setErrors(check());
          }}
          onBlur={() => {
            if (submitted) setErrors(check());
          }}
        />

        <button type="submit" className={PRIMARY_BUTTON_CLASS} disabled={busy}>
          {busy ? "Saving…" : "Reset Password"}
        </button>
      </form>
    </AuthShell>
  );
}
